import { api } from "@/shared/services/api";
import { useAuthStore } from "@/shared/stores/authStore";

export class FetchError extends Error {
  status: number;
  info: any;
  constructor(message: string, status: number, info: any) {
    super(message);
    this.status = status;
    this.info = info;
  }
}

export class RedirectError extends FetchError {
  constructor(message: string, status: number, info: any) {
    super(message, status, info);
  }
}

const DEFAULT_AUTH_ERROR_MSG =
  "An error occurred while fetching the data, related to the user's authentication status.";

const DEFAULT_ERROR_MSG = "An error occurred while fetching the data.";

/**
 * Retry callback for TanStack Query — suppresses retries for auth errors.
 * Pass as `retry` option: `retry: (failureCount, error) => skipRetryOnAuthError(failureCount, error)`
 */
export const skipRetryOnAuthError = (
  failureCount: number,
  error: Error
): boolean => {
  if (
    error instanceof FetchError &&
    (error.status === 401 || error.status === 403)
  ) {
    return false; // do not retry
  }
  return failureCount < 2; // retry up to 2 times for other errors
};

// Best-effort JSON parse — rate-limit / proxy error responses are often
// plain text ("Too Many Requests"), and res.json() throwing a parse error
// there would mask the real status behind a confusing SyntaxError.
async function safeParseJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return { message: await res.text().catch(() => res.statusText) };
  }
}

export const errorHandlingFetcher = async <T>(url: string): Promise<T> => {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(url, { headers });

  if (res.status === 403) {
    const redirect = new RedirectError(
      DEFAULT_AUTH_ERROR_MSG,
      res.status,
      await safeParseJson(res)
    );
    throw redirect;
  }

  if (res.status === 429) {
    const retryAfter = res.headers.get("Retry-After");
    throw new FetchError("Rate limited", res.status, {
      ...(await safeParseJson(res)),
      retryAfter,
    });
  }

  if (!res.ok) {
    const error = new FetchError(
      DEFAULT_ERROR_MSG,
      res.status,
      await safeParseJson(res)
    );
    throw error;
  }

  return res.json();
};

/**
 * Default SWR fetcher: GET JSON through the shared ky `api` client (Bearer
 * token, 401 handling, retry). SWR keys are written as absolute API paths
 * (`/api/v1/...`); the leading `/api` is dropped because `api` already
 * prefixes `API_BASE`.
 */
export const fetcher = <T = unknown>(
  key: string,
  init?: RequestInit
): Promise<T> =>
  api(key.replace(/^\/api(?=\/)/, "").replace(/^\/+/, ""), init).json<T>();
